import { axios } from 'Lib/axios'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { ConnectionState } from 'Types/Product'
import { bzAxios } from 'Lib/bzAxios'
import { today, getCustomDate, getPastFormattedDate } from 'Utils/dates'
import { baseRangeOptions } from 'Components/Elements/DateRange'
import {
  AccountPlanData,
  BotzappingDataResponse,
  dashboardSummaryParams,
  GetDashboardSummaryResponse,
} from './types'
import { getUnixTime } from 'date-fns'
import { DEFAULT_TIME_ZONE } from '../routes/Home/utils'
import { replaceWWWS } from 'Utils/stringHelper'
import { useQuery } from '@tanstack/react-query'
import { ExtractFnReturnType, QueryConfig } from 'Lib/react-query'

type GetSuiteData = {
  paidMarketingConnectionStage: ConnectionState
  botZappingConnectionStage: ConnectionState
}

export const getSuiteData = (): Promise<GetSuiteData | Error> => {
  const accountId = useUserStore.getState().accountId
  const userId = useUserStore.getState().userId
  return axios
    .get<{ data: GetSuiteData }>(`users/${userId}/accounts/${accountId}/suite/status`)
    .then((res) => res.data.data)
    .catch((e) => {
      return e as Error
    })
}

export const getAccountPlan = (): Promise<AccountPlanData | Error> => {
  const accountId = useUserStore.getState().accountId
  return axios
    .get<{ data: AccountPlanData }>(`/accounts/${accountId}/subscriptions/packages/limits`)
    .then((res) => res.data.data)
    .catch((e) => {
      return e as Error
    })
}

const weekRange = baseRangeOptions.WEEK.range

export const dashboardSummary: dashboardSummaryParams = {
  endDate: weekRange?.to ? getUnixTime(weekRange?.to) : undefined,
  startDate: weekRange?.from ? getUnixTime(weekRange?.from) : undefined,
  timezone: DEFAULT_TIME_ZONE,
  domains: [],
  campaigns: [],
}

export const getDashboardSummary = (
  params?: dashboardSummaryParams
): Promise<GetDashboardSummaryResponse | Error> => {
  const { accountId, userId, owner } = useUserStore.getState()
  const userParams = owner ? { ...params, domains: [] } : params
  return axios
    .post<{ data: GetDashboardSummaryResponse }>(
      `/users/${userId}/accounts/${accountId}/dashboard/get-summary`,
      {
        ...dashboardSummary,
        ...userParams,
      }
    )
    .then((res) => res.data.data)
    .catch((e) => {
      return e as Error
    })
}

export const BotzappingDateParams = {
  fromDate: 'from-date',
  toDate: 'to-date',
  groupByDomain: 'groupByDomain',
} as const

export const summaryParams: BotzappingParams = {
  [BotzappingDateParams.fromDate]: getPastFormattedDate(6),
  [BotzappingDateParams.toDate]: today(),
  offset: 0,
  limit: 300,
  last3months: false,
  domains: [],
  groupbydomain: true,
}

type BotzappingParams = {
  [BotzappingDateParams.fromDate]: string
  [BotzappingDateParams.toDate]: string
  offset: number
  limit: number
  last3months: boolean
  domains?: Array<string>
  groupbydomain: boolean
}

// TODO: REMOVE ONCE REFCATORING IS DONE
export const getBotzappingData = (
  startDate?: number,
  endDate?: number,
  groupByDomain?: boolean,
  domains?: Array<string>
): Promise<BotzappingDataResponse | Error> => {
  const { networkId, owner } = useUserStore.getState()
  const { fromDate, toDate } = BotzappingDateParams
  const params = {
    ...summaryParams,
    [fromDate]: startDate ? getCustomDate(startDate) : summaryParams[BotzappingDateParams.fromDate],
    [toDate]: endDate ? getCustomDate(endDate) : summaryParams[BotzappingDateParams.toDate],
    groupbydomain: groupByDomain,
    domains: owner ? null : domains ? replaceWWWS(domains) : null,
  }
  return bzAxios
    .get<BotzappingDataResponse>(`/summary?networkIdEnc=${networkId}&groupbydomain=true`, {
      params,
    })
    .then((res) => {
      return res.data
    })
    .catch((e) => {
      return e as Error
    })
}

export const getBzBotMitigationData = ({
  params,
  networkId,
}: {
  params: { startDate?: number; endDate?: number; groupByDomain?: boolean; domains?: Array<string> }
  networkId: string
}): Promise<BotzappingDataResponse | Error> => {
  return bzAxios
    .get<BotzappingDataResponse>(`/summary?networkIdEnc=${networkId}`, {
      params,
    })
    .then((res) => {
      return res.data
    })
    .catch((e) => {
      return e as Error
    })
}

type QueryFnType = typeof getBzBotMitigationData

export const useGetBotMitigationData = ({
  startDate,
  endDate,
  groupByDomain,
  domains,
  config,
}: {
  startDate?: number
  endDate?: number
  groupByDomain?: boolean
  domains?: Array<string>
  config?: QueryConfig<QueryFnType>
}) => {
  const { networkId, owner } = useUserStore()
  const { fromDate, toDate } = BotzappingDateParams
  const params = {
    ...summaryParams,
    [fromDate]: startDate ? getCustomDate(startDate) : summaryParams[BotzappingDateParams.fromDate],
    [toDate]: endDate ? getCustomDate(endDate) : summaryParams[BotzappingDateParams.toDate],
    groupbydomain: groupByDomain,
    domains: owner ? undefined : domains ? replaceWWWS(domains) : undefined,
  }
  const query = useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: ['getBzBotMitigationData', networkId, startDate, endDate, groupByDomain, domains],
    queryFn: () => getBzBotMitigationData({ params, networkId }),
    ...config,
  })

  const data = query.data instanceof Error ? undefined : query.data

  return { ...query, data }
}
